/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * Display 필드 클래스를 정의한다.
 *
 * @file /src/components/form/field/display/Display.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 9. 25.
 */
import * as parent from '@components/form/field/Field';
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';
import { Language, LanguageText } from '@utils/Language';
import './Display.css';

export interface Listeners<T> extends parent.Listeners<T> {}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 필드값
     */
    value?: LanguageText;

    /**
     * 필드내 텍스트 정렬
     */
    textAlign?: string;

    /**
     * 필드 스타일시트
     */
    textClass?: string;

    /**
     * 텍스트 줄바꿈여부
     */
    textWrap?: boolean;

    /**
     * 필드 랜더러
     */
    renderer?: (value: any, field: Display) => string | Promise<string>;

    /**
     * 핸들러
     */
    handler?: (value: any, field: Display) => void | Promise<void>;

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class Display extends parent.Field {
    /**
     * 컴포넌트 확장타입
     */
    variant = 'display';

    /**
     * 필드내 텍스트 정렬
     */
    textAlign: string;

    /**
     * 필드 스타일시트
     */
    textClass: string;

    /**
     * 텍스트 줄바꿈여부
     */
    textWrap: boolean;

    /**
     * 필드 랜더러
     */
    renderer: (value: any, field: Display) => string | Promise<string>;

    /**
     * 핸들러
     */
    handler: (value: any, field: Display) => void | Promise<void>;

    /**
     * Display Dom
     */
    $display: Dom = null;

    /**
     * Display 필드 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<Display> = null) {
        super(properties);

        this.textAlign = this.properties?.textAlign ?? null;
        this.textClass = this.properties?.textClass ?? null;
        this.textWrap = this.properties?.textWrap !== false;
        this.renderer = this.properties?.renderer ?? null;
        this.handler = this.properties?.handler ?? null;
    }

    /**
     * DISPLAY 필드 DOM 을 가져온다.
     *
     * @returns DISPLAY 필드 DOM
     */
    $getDisplay(): Dom {
        if (this.$display === null) {
            this.$display = Html.create('div');
            this.$display.setStyle('text-align', this.textAlign);

            if (this.textWrap == false) {
                this.$display.addClass('nowrap');
            }

            if (this.textClass !== null) {
                this.$display.addClass(...this.textClass.split(' '));
            }

            if (this.handler !== null) {
                this.$display.addClass('handler');
                this.$display.on('click', async () => {
                    if (this.$display.hasClass('loading') == false) {
                        this.$display.addClass('loading');
                        await this.handler(await this.getValue(), this);
                        this.$display.removeClass('loading');
                    }
                });
            }
        }

        return this.$display;
    }

    /**
     * 필드값을 지정한다.
     *
     * @param value - 값
     * @param is_init - 초기값여부
     */
    async setValue(value: LanguageText | any, is_init: boolean = false): Promise<void> {
        await this.lockValue();

        if (this.renderer === null) {
            value = await Language.resolveText(value);
            this.$getDisplay().html(value);
        } else {
            this.$getDisplay().html(await this.renderer(value, this));
        }

        await this.releaseValue(value, is_init);
    }

    /**
     * 필드영역을 렌더링한다.
     */
    async renderField(): Promise<void> {
        this.$getContent().append(this.$getDisplay());
    }
}
